import React, { useState } from "react";
import { FiX, FiArrowLeft, FiCheck, FiAlertTriangle, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { parseQuestionsFromText } from "../../../utils/questionExportImport";

const TYPE_BADGES = {
  multiple_choice: { label: "Multiple Choice", color: "bg-blue-100 text-blue-700" },
  true_or_false: { label: "True / False", color: "bg-green-100 text-green-700" },
  text: { label: "Open Answer", color: "bg-purple-100 text-purple-700" },
};

const PLACEHOLDER = `1. What is the capital of France?
A. London
B. Paris
C. Berlin
D. Rome
Answer: B

2. The Earth revolves around the Sun.
A. True
B. False
Answer: A

3. Explain the process of photosynthesis.
Answer: Plants convert sunlight into energy...`;

const PasteQuestionsModal = ({ onClose, onConfirm }) => {
  const [rawText, setRawText] = useState("");
  const [parsedQuestions, setParsedQuestions] = useState(null);
  const [parseError, setParseError] = useState("");
  const [step, setStep] = useState("input"); // "input" | "preview"
  const [showFormats, setShowFormats] = useState(false);

  const handleParse = () => {
    setParseError("");
    try {
      const questions = parseQuestionsFromText(rawText);
      if (questions.length === 0) {
        setParseError("No valid questions found. Please check the format and try again.");
        return;
      }
      setParsedQuestions(questions);
      setStep("preview");
    } catch (err) {
      setParseError(err.message || "Failed to parse questions.");
    }
  };

  const handleConfirm = () => {
    onConfirm(parsedQuestions);
    onClose();
  };

  const missingAnswerCount = parsedQuestions
    ? parsedQuestions.filter(q => !q.correct_answer).length
    : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {step === "preview" && (
              <button
                onClick={() => setStep("input")}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                title="Back to input"
              >
                <FiArrowLeft size={18} />
              </button>
            )}
            <h2 className="text-lg font-semibold text-gray-900">
              {step === "input"
                ? "Paste Questions"
                : `Review Parsed Questions (${parsedQuestions?.length || 0} found)`}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {step === "input" ? (
            <>
              <p className="text-sm text-gray-500 mb-3">
                Paste questions from Word, Google Docs, or any text source. Supports numbered lists with A/B/C/D options.
              </p>

              <textarea
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                placeholder={PLACEHOLDER}
                rows={14}
                className="w-full border border-gray-200 rounded-xl p-4 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />

              {parseError && (
                <div className="mt-3 flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                  <FiAlertTriangle className="flex-shrink-0 mt-0.5" size={14} />
                  <span>{parseError}</span>
                </div>
              )}

              {/* Collapsible format hints */}
              <button
                onClick={() => setShowFormats(!showFormats)}
                className="mt-3 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showFormats ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}
                Supported formats
              </button>
              {showFormats && (
                <div className="mt-2 bg-gray-50 rounded-lg p-3 text-xs text-gray-500 space-y-2">
                  <p><strong>Numbered + Answer line:</strong> 1. Question?\nA. Option\nAnswer: B</p>
                  <p><strong>Parentheses:</strong> 1) Question?\na) Option\nb) Option\nAnswer: a</p>
                  <p><strong>Asterisk correct:</strong> A. Wrong\nB. Correct *\nC. Wrong</p>
                  <p><strong>True/False:</strong> A. True\nB. False\nAnswer: A</p>
                  <p><strong>Open-ended:</strong> Just the question text with no options</p>
                </div>
              )}
            </>
          ) : (
            <>
              {missingAnswerCount > 0 && (
                <div className="mb-4 flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <FiAlertTriangle className="flex-shrink-0 mt-0.5" size={14} />
                  <span>
                    {missingAnswerCount} question{missingAnswerCount > 1 ? "s" : ""} could not have{" "}
                    {missingAnswerCount > 1 ? "their answers" : "its answer"} auto-detected.
                    You can set {missingAnswerCount > 1 ? "these" : "this"} manually after import.
                  </span>
                </div>
              )}

              <div className="space-y-3">
                {parsedQuestions?.map((q, idx) => {
                  const badge = TYPE_BADGES[q.type] || TYPE_BADGES.multiple_choice;
                  return (
                    <div
                      key={q.id}
                      className="border border-gray-200 rounded-xl p-4"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <p className="text-sm font-medium text-gray-800 line-clamp-2">
                          <span className="text-gray-400 mr-1.5">Q{idx + 1}.</span>
                          {q.question}
                        </p>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${badge.color}`}>
                          {badge.label}
                        </span>
                      </div>

                      {q.type === "multiple_choice" && q.options.length > 0 && (
                        <div className="ml-6 space-y-1 mb-2">
                          {q.options.map((opt, oi) => {
                            const isCorrect = opt === q.correct_answer;
                            return (
                              <div
                                key={oi}
                                className={`text-xs flex items-center gap-1.5 ${
                                  isCorrect ? "text-green-700 font-medium" : "text-gray-500"
                                }`}
                              >
                                {isCorrect && <FiCheck size={12} className="flex-shrink-0" />}
                                <span>
                                  {String.fromCharCode(65 + oi)}. {opt}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {q.type === "true_or_false" && (
                        <div className="ml-6 text-xs text-gray-500 mb-2">
                          Answer:{" "}
                          <span className={q.correct_answer ? "text-green-700 font-medium" : "text-amber-600"}>
                            {q.correct_answer ? (q.correct_answer === "true" ? "True" : "False") : "Not detected"}
                          </span>
                        </div>
                      )}

                      {q.type === "text" && q.correct_answer && (
                        <div className="ml-6 text-xs text-gray-500 mb-2">
                          Expected: <span className="text-green-700 font-medium">{q.correct_answer}</span>
                        </div>
                      )}

                      {!q.correct_answer && q.type !== "text" && (
                        <div className="ml-6 text-xs text-amber-600">
                          Answer not detected
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          {step === "input" ? (
            <button
              onClick={handleParse}
              disabled={!rawText.trim()}
              className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Parse Questions
            </button>
          ) : (
            <button
              onClick={handleConfirm}
              className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Add {parsedQuestions?.length || 0} Question{parsedQuestions?.length !== 1 ? "s" : ""}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasteQuestionsModal;
